import { useEffect, useState } from "react";
import { FileClock } from "lucide-react";

const KEY_PREFIX = "brillencrm:draft:";

const readHasDraft = (storageKey) => {
  if (typeof window === "undefined") return false;
  try {
    return !!window.localStorage.getItem(`${KEY_PREFIX}${storageKey}`);
  } catch {
    return false;
  }
};

/**
 * Small pill shown next to a "Create X" button when a saved draft exists in
 * localStorage under `${KEY_PREFIX}${storageKey}`. Clicking it invokes
 * `onResume` (usually the same handler as the create button, which will
 * restore the draft).
 *
 * The badge live-updates whenever the underlying draft is written or cleared
 * (via the `brillencrm:draft-changed` custom event dispatched by useFormDraft),
 * and also across tabs via the browser `storage` event.
 */
export function DraftIndicatorBadge({ storageKey, onResume, label = "Unsaved draft" }) {
  const [hasDraft, setHasDraft] = useState(() => readHasDraft(storageKey));

  useEffect(() => {
    const fullKey = `${KEY_PREFIX}${storageKey}`;
    const refresh = () => setHasDraft(readHasDraft(storageKey));

    const onStorage = (e) => {
      if (e.key === fullKey) refresh();
    };
    const onCustom = (e) => {
      if (e.detail?.key === fullKey) refresh();
    };

    // Refresh once on mount in case the value changed before this component subscribed.
    refresh();

    window.addEventListener("storage", onStorage);
    window.addEventListener("brillencrm:draft-changed", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("brillencrm:draft-changed", onCustom);
    };
  }, [storageKey]);

  if (!hasDraft) return null;

  return (
    <button
      type="button"
      onClick={onResume}
      title="Resume your unsaved draft"
      data-testid={`draft-indicator-${storageKey}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50 transition-colors"
    >
      <FileClock className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
